import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../../config';
import { toast } from 'react-toastify';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { StreamCard } from './StreamCard';
import { useObserver } from '../../hooks/useObserver';
import { resolveStreamFilters, saveStreamFiltersToLocalStorage } from './utils/filters';

function Streams() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const { gender, sort } = resolveStreamFilters(searchParams);

  const [streams, setStreams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [totalItems, setTotalItems] = useState(null);
  const [page, setPage] = useState(1);
  const isLoadingStreamsRef = useRef(false);

  const fetchActiveStreams = useCallback(
    async ({ genderValue, sortValue, nextPage = 1, replace = false }) => {
      if (isLoadingStreamsRef.current) return;
      isLoadingStreamsRef.current = true;

      try {
        const response = await axiosPrivate.get(`/stream?gender=${genderValue}&sort=${sortValue}&page=${nextPage}`);

        const streamData = response.data?.data?.items || response.data?.data || [];
        setStreams((prev) => {
          if (replace) {
            return streamData;
          }

          const nextStreams = new Map(prev.map((stream) => [stream._id, stream]));
          streamData.forEach((stream) => {
            nextStreams.set(stream._id, stream);
          });

          return Array.from(nextStreams.values());
        });
        setTotalItems(response.data?.data?.total ?? streamData.length);
        setPage(nextPage);
      } catch (error) {
        console.error('Error fetching active streams:', error);
        toast.error('Не удалось загрузить список стримов');
      } finally {
        isLoadingStreamsRef.current = false;
      }
    },
    [axiosPrivate],
  );

  const targetRef = useObserver({
    onIntersecting: () => {
      if (isLoadingStreamsRef.current || streams.length === 0 || totalItems === null || streams.length >= totalItems) {
        return;
      }

      fetchActiveStreams({ genderValue: gender, sortValue: sort, nextPage: page + 1 });
    },
    rootMargin: '100px 0px 0px 0px',
  });

  useEffect(() => {
    saveStreamFiltersToLocalStorage({ gender, sort });
    setStreams([]);
    setTotalItems(null);
    setPage(1);
    setIsLoading(true);

    fetchActiveStreams({ genderValue: gender, sortValue: sort, nextPage: 1, replace: true }).finally(() => {
      setIsLoading(false);
    });

    const interval = setInterval(() => {
      fetchActiveStreams({ genderValue: gender, sortValue: sort, nextPage: 1, replace: true });
    }, 1000 * 60);

    return () => {
      clearInterval(interval);
    };
  }, [fetchActiveStreams, gender, sort]);

  return (
    <div className="flex flex-col justify-center items-center w-[100%] min-h-[60vh] relative">
      {isLoading && streams.length <= 0 && (
        <div className="flex items-center justify-center h-full">
          <DotSpinner />
        </div>
      )}
      {!isLoading && streams.length <= 0 && (
        <div className="flex flex-col items-center h-full px-4">
          <img src="/icons/stream-not-found.svg" alt="meet-not-found" className="w-[100px]" />
          <p className="text-center font-semibold text-[18px] text-white mt-3">
            Ой, кажется, все стримеры сейчас отдыхают. Стань первым, кто выйдет в эфир!
          </p>
        </div>
      )}

      {streams.length > 0 && (
        <div className="grid grid-cols-2 gap-4 relative w-full px-4 h-full mt-[12px] mb-[120px]">
          {streams.map((stream) => (
            <StreamCard key={stream._id} stream={stream} onClick={(id) => navigate(config.ROUTES.STREAMS.WATCH(id))} />
          ))}
        </div>
      )}
      <div ref={targetRef} className="h-2" />
      <img
        src="/icons/strimBtn.svg"
        alt="Начать трансляцию"
        onClick={() => navigate(`${config.ROUTES.STREAMS.BROADCASTER}`)}
        className="fixed bottom-[112px] right-[24px]"
      />
    </div>
  );
}

export default Streams;
